// NIGHT-DIRECTSTAFF-2026-10-25c §2: Reports -> Credentials -- every current credential with an expiry (onboarding_documents, the
// newest of each kind + state per candidate: lib/credentials.js currentCredentials), grouped: already expired, within 14 / 30 / 60 /
// 90 days. Each row: the candidate's id, the credential, the state, the date, and "Chased" from the agents' own markers
// (recruiter-agent credential-watch.js: agent_lead_decisions decision 'credential_watch', reason "<d60|d30|d14>:<document id>@<expires_on>"
// -- d60 / d30 a text to the candidate, d14 a staff item). Test and merged candidates are left out.
import { currentCredentials, daysLeftOf, credLabel } from './credentials';

export const GROUPS = [['expired', 'Already expired'], [14, 'Within 14 days'], [30, 'Within 30 days'], [60, 'Within 60 days'], [90, 'Within 90 days']];
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const isTest = (c) => !!c && (!!c.test_marked_at || (Array.isArray(c.tags) && c.tags.some((x) => s(x).toLowerCase() === 'test')));
const CHASE = { d60: 'Texted at 60 days', d30: 'Texted at 30 days', d14: 'Staff item at 14 days' };
const MARK_RE = /^(d60|d30|d14):(\d+)@(\d{4}-\d{2}-\d{2})$/;

/** decisions -> Map("<doc id>@<date>" -> ['d60', 'd30', ...]) */
export function chaseMarks(decisions = []) {
  const out = new Map();
  for (const d of decisions || []) {
    if (s(d.decision) !== 'credential_watch') continue;
    const m = MARK_RE.exec(s(d.reason));
    if (!m) continue;
    const k = `${m[2]}@${m[3]}`;
    out.set(k, [...new Set([...(out.get(k) || []), m[1]])]);
  }
  return out;
}
export const chasedText = (marks) => (marks && marks.length ? ['d14', 'd30', 'd60'].filter((w) => marks.includes(w)).map((w) => CHASE[w]).join(' · ') : 'Not chased yet');

/** -> [{ id, title, rows: [{ key, candidate, kind, label, state, expires_on, days, chased }] }] (empty groups left out) */
export function credentialGroups({ documents = [], candidates = [], decisions = [], now = Date.now() } = {}) {
  const byId = new Map((candidates || []).map((c) => [String(c.id), c]));
  const marks = chaseMarks(decisions);
  const rows = [];
  for (const d of currentCredentials(documents)) {
    const days = daysLeftOf(d.expires_on, now);
    if (days === null || days > 90) continue;
    const c = byId.get(String(d.candidate_id));
    if (!c || isTest(c) || c.merged_into) continue;
    const m = marks.get(`${d.id}@${s(d.expires_on)}`) || [];
    rows.push({ key: `${d.id}`, documentId: d.id, candidate: c, kind: d.kind, label: credLabel(d), state: d.license_state || '', expires_on: d.expires_on, days, chased: chasedText(m), marks: m });
  }
  const groupOf = (days) => (days < 0 ? 'expired' : [14, 30, 60, 90].find((w) => days <= w));
  return GROUPS.map(([id, title]) => ({ id, title, rows: rows.filter((r) => groupOf(r.days) === id).sort((a, b) => a.days - b.days) })).filter((g) => g.rows.length);
}

// DAY-DIRECTSTAFF-2026-11-12 §1: the candidate page names the same windows as Numbers and Reports -> Credentials (GROUPS), with a colour
// for each: expired red; within 14 / 30 days amber; within 60 / 90 days brown; later green; no date grey. -> { label, tone }
export function expiryWindow(days) {
  if (days === null || days === undefined || Number.isNaN(days)) return { label: '', tone: '#475569' };
  if (days < 0) return { label: 'Expired', tone: '#b91c1c' };
  const w = [14, 30, 60, 90].find((n) => days <= n);
  return { label: w ? `Within ${w} days` : '', tone: w === undefined ? '#166534' : w <= 30 ? '#b45309' : '#854d0e' };
}

// NIGHT-DIRECTSTAFF-2026-10-28b §1: Numbers' counts and the one-tap renewal email.
/** The list's own rows (test and merged out), counted: expired, and within 30 / 60 / 90 days (each includes the shorter). */
export function expiryCounts({ documents = [], candidates = [], now = Date.now() } = {}) {
  const rows = credentialGroups({ documents, candidates, now }).flatMap((g) => g.rows);
  const within = (n) => rows.filter((r) => r.days >= 0 && r.days <= n).length;
  return { expired: rows.filter((r) => r.days < 0).length, d30: within(30), d60: within(60), d90: within(90) };
}

/** The email: what expires and when, and the ask -- send the renewal (no link: they reply with it, as the reminders do). */
export function renewalRequestMessage(name, { label, expires_on: on }) {
  const f = s(name).split(/\s+/)[0];
  const hi = /^[A-Za-z][A-Za-z'.-]*$/.test(f) ? `Hi ${f},` : 'Hi,';
  const when = new Date(`${on}T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const past = Date.parse(`${on}T23:59:59Z`) < Date.now();
  return `${hi} your ${label} on file with ProTherapy Staffing ${past ? 'expired' : 'expires'} ${when}. Please upload the renewal: reply to this email with a photo or PDF of it, and we'll update your file. Thank you!`;
}

// lib/sendLink.js sendNote('credential_expiry', <document id>, 'email', by): "Expiry reminder (document 12) sent by email by x@y"
const EMAILED_RE = /^Expiry reminder \(document (\d+)\) sent by email(?= by |$)/;
/** activity_log rows -> Map(document id -> the newest date a renewal email went) */
export function emailedMarks(activity = []) {
  const out = new Map();
  for (const a of activity || []) {
    const m = EMAILED_RE.exec(s(a.note));
    if (!m) continue;
    const at = s(a.created_at) || s(a.date);
    if (!out.has(m[1]) || at > out.get(m[1])) out.set(m[1], at);
  }
  return out;
}
